<template>
  <vx-card class="editor-mid-card">
    <div class="mb-base">
      <div class="flex justify-between flex-wrap mb-1">
        <vs-row vs-w="6">
          <h3 class="float-left mr-4">{{ $t("editor.about.title") }}</h3>
          <vx-tooltip v-if="saveInProgress" text="Saving to cloud...">
            <feather-icon
              icon="CloudIcon"
              class="save-in-progress-status-icon"
              svgClasses="text-success"
          /></vx-tooltip>
          <vx-tooltip v-else :text="$t('editor.about.saveToCloud')"
            ><feather-icon
              icon="CloudIcon"
              class="save-in-progress-status-icon"
          /></vx-tooltip>
        </vs-row>
        <div>
          <options previousMove="none" :nextMove="{ name: 'editor-contact' }" />
        </div>
      </div>
      <p v-if="!isUserLoggedIn">
        <span v-if="organizationSlug === 'default'">
          {{ $t("editor.utils.notAuthenticated.youAreNotAuthenticated") }}
          <router-link :to="{ name: 'editor-about' }">{{ $t("editor.utils.notAuthenticated.login") }}</router-link>
          {{ $t("editor.utils.notAuthenticated.toSaveYourData") }}
        </span>
        <span v-else-if="organizationSlug === 'gala'">
          {{ $t("editor.utils.notAuthenticated.youAreNotAuthenticated") }}
          <router-link :to="{ name: 'editor-about-org' }">{{ $t("editor.utils.notAuthenticated.login") }}</router-link>
          {{ $t("editor.utils.notAuthenticated.toSaveYourData") }}
        </span>
        <span v-else>
          {{ $t("editor.utils.notAuthenticated.youAreNotAuthenticated") }}
          <router-link :to="{ name: 'editor-about-custom' }">{{ $t("editor.utils.notAuthenticated.login") }}</router-link>
          {{ $t("editor.utils.notAuthenticated.toSaveYourData") }}
        </span>
      </p>
    </div>

    <div v-if="!recordVideoPitch" class="main-form">
      <div class="vx-row">
        <div class="vx-col mb-base w-full md:w-1/2">
          <div class="mb-base" v-if="isUserLoggedIn">
            <label>{{ $t("editor.about.email") }}</label>
            <vs-row id="v-step-0">
              <p class="mr-3">{{ localData.email }}</p>
              <vx-tooltip :text="$t('editor.about.youHaveBeenSuccessfullyAuthenticated')" position="bottom">
                <feather-icon class="text-success" icon="CheckCircleIcon" /> </vx-tooltip>
            </vs-row>
          </div>
          <div class="mb-base" v-else-if="!otpSent || isUserLoggedIn" id="v-step-0-1">
            <label>{{ $t("editor.about.email") }}<span class="required-field">*</span></label>
            <vx-input-group>
              <vs-input v-validate="'required|email|min:3'" :placeholder="$t('editor.about.enterYourEmailIdToLogin')"
                data-vv-validate-on="blur" :disabled="otpSent && isUserLoggedIn" name="Email" icon-no-border
                icon="icon icon-mail" icon-pack="feather" v-model="localData.email"
                class="w-full mr-3 dark-input-field" />

              <template slot="append" v-if="!isUserLoggedIn">
                <div class="append-text btn-addon">
                  <vs-button class="btn btn-icon btn-primary" :disabled="!localData.email" @click="sendMeOTP()">
                    <vx-tooltip :text="$t('editor.about.clickToAuthenticate')" position="bottom">
                      <feather-icon icon="LogInIcon" />
                    </vx-tooltip>
                  </vs-button>
                </div>
              </template>
              <template slot="append" v-else>
                <div class="append-text btn-addon">
                  <vs-button color="#28C76F" class="btn btn-icon btn-primary">
                    <vx-tooltip :text="$t('editor.about.youHaveBeenSuccessfullyAuthenticated')" position="bottom">
                      <feather-icon icon="CheckCircleIcon" />
                    </vx-tooltip>
                  </vs-button>
                </div>
              </template>
            </vx-input-group>
            <span class="text-danger text-sm">{{ errors.first("Email") }}</span>
          </div>
          <div class="mb-base" v-else>
            <label>{{ $t("editor.about.otp.title") }}</label>
            <vx-input-group class="mb-base">
              <vs-input v-validate="'required'" data-vv-validate-on="blur" :placeholder="$t('editor.about.enterOTP')"
                name="OTP" icon-no-border icon="icon icon-unlock" icon-pack="feather" v-model="localData.otp"
                class="w-full mr-3 dark-input-field" />

              <template slot="append">
                <div class="append-text btn-addon">
                  <vs-button class="btn btn-icon btn-primary" @click="verifyOTP()">
                    {{ $t("editor.about.otp.verify") }}
                  </vs-button>
                </div>
              </template>
            </vx-input-group>
          </div>
          
          <vs-row>
            <vs-col class="w-full">
              <div class="flex justify-between flex-wrap">
                
                <!-- Profile Picture Component With UI Hint -->
                <div class="flex flex-col items-center">
                  <user-profile-picture 
                    :profilePictureUrl="localData.profilePictureUrl" 
                    @picture-updated="handlePictureUpdate" 
                  /> 
                  <!-- Size Hint UI Added Below -->
                  <div class="mt-2 text-center w-full">
                    <span class="text-xs font-medium flex items-center justify-center whitespace-nowrap" style="color: #848199;">
                      <feather-icon icon="InfoIcon" svgClasses="w-3 h-3 mr-1" />
                      <!-- UPDATED: Change text from 1MB to 5MB -->
                      Max Size: 5MB
                    </span>
                  </div>               
                </div>

                <div class="w-full md:w-2/3 mt-4 md:mt-0">
                  <label>{{ $t("editor.about.dob") }}</label>
                  <vs-input v-validate="'min:10'" data-vv-validate-on="blur" name="DOB" :max="maxDate" type="date" icon-no-border v-model="localData.dob"
                    class="w-full mr-3 dark-input-field" @blur="saveField('saveDob')" :disabled="disableInputField" />
                  <vs-row class="mt-base">
                    <vs-radio class="mr-5" name="Gender" v-model="localData.gender" vs-value="female" @blur="saveField('saveGender')" :disabled="disableInputField">
                      <vx-tooltip :text="$t('editor.about.gender.female')" position="bottom">
                        <img src="@/assets/images/icons/gender/female.svg" width="20px" height="20px" />
                      </vx-tooltip>
                    </vs-radio>
                    <vs-radio class="mr-5" name="Gender" v-model="localData.gender" vs-value="male" @blur="saveField('saveGender')" :disabled="disableInputField">
                      <vx-tooltip :text="$t('editor.about.gender.male')" position="bottom">
                        <img src="@/assets/images/icons/gender/male.svg" width="20px" height="20px" />
                      </vx-tooltip>
                    </vs-radio>
                    <vs-radio name="Gender" v-model="localData.gender" vs-value="other" @blur="saveField('saveGender')" :disabled="disableInputField">
                      <vx-tooltip :text="$t('editor.about.gender.other')" position="bottom">
                        <img src="@/assets/images/icons/gender/circle-ring.svg" width="20px" height="20px" />
                      </vx-tooltip>
                    </vs-radio>
                  </vs-row>
                </div>
              </div>
            </vs-col>
          </vs-row>
        </div>
        <div class="vx-col mb-base w-full md:w-1/2">
          <!-- resume parser  -->
          <div id="v-step-2">
            <div class="upload-btn-wrapper items-center w-full">
              <img class="upload-resume-icon" src="@/assets/images/icons/upload-resume.svg" />
              <button class="btn w-full" :class="{ 'upload-success': upload_success }" />
              <div class="resume-parser-text absolute text-center w-full">
                <p class="my-2">{{ $t("editor.about.resumeParser.title") }}</p>
                <p>{{ $t("editor.about.resumeParser.size") }}</p>
              </div>
              <input type="file" name="myfile" id="file" ref="file" class="h-full w-full" @change="selectFieldPopup()" @drag="selectFieldPopup()" @drop="selectFieldPopup()" />
            </div>
          </div>
        </div>
      </div>
      <div class="vx-row">
        <div id="v-step-1" class="vx-col mb-base w-full md:w-1/2">
          <label>{{ $t("editor.about.firstName") }}<span class="required-field">*</span></label>
          <vs-input class="w-full dark-input-field" name="First Name" maxlength="50" data-vv-validate-on="blur" v-validate="'required'" :disabled="disableInputField" v-model="localData.firstName" @blur="saveField('saveFirstName', 'First Name')" />
          <span class="text-danger text-sm">{{ errors.first("First Name") }}</span>
        </div>
        <div class="vx-col mb-base w-full md:w-1/2">
          <label>{{ $t("editor.about.lastName") }}<span class="required-field">*</span></label>
          <vs-input class="w-full dark-input-field" name="Last Name" maxlength="50" data-vv-validate-on="blur" v-validate="'required'" :disabled="disableInputField" v-model="localData.lastName" @blur="saveField('saveLastName', 'Last Name')" />
          <span class="text-danger text-sm">{{ errors.first("Last Name") }}</span>
        </div>
      </div>
      <div class="vx-row">
        <div class="vx-col sm-mb-base w-full md:w-1/2">
          <label>{{ $t("editor.about.careerObjective.title") }}<span class="required-field">*</span></label>
          <vs-textarea class="w-full" height="175px" name="Career Objective" maxlength="2600" data-vv-validate-on="blur" v-validate="'required'" :disabled="disableInputField" v-model="localData.careerObjective" :placeholder="$t('editor.about.careerObjective.placeholder')" @blur="saveField('saveCareerObjective', 'Career Objective')" />
          <span class="text-danger text-sm">{{ errors.first("Career Objective") }}</span>
        </div>
        <div class="vx-col sm-mb-base w-full md:w-1/2">
          <vs-row class="mb-base">
            <label>{{ $t("editor.about.profession") }}</label>
            <vs-input class="w-full dark-input-field" name="Profession" maxlength="220" :disabled="disableInputField" v-model="localData.profession" @blur="saveField('saveProfession')" />
          </vs-row>
          <vs-row>
            <label>{{ $t("editor.about.about.title") }}</label>
            <vs-textarea class="about--textarea w-full" height="80px" name="About" maxlength="2600" :disabled="disableInputField" v-model="localData.about" :placeholder="$t('editor.about.about.placeholder')" @blur="saveField('saveAbout')" />
          </vs-row>
        </div>
      </div>
    </div>
    <div v-else class="main-form">
      <small class="mb-1 cursor-pointer float-right" @click="toggleVideoPitch()">Close</small>
      <record-video />
    </div>
  </vx-card>
</template>

<script>
import FeatherIcon from "../../components/FeatherIcon.vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import Options from "./components/Options.vue";
import constants from "@/constants";
import { sendMeOTPService, verifyOTPService } from "@/services/auth.service.js";
import { saveInputFieldService, updateImageService } from "@/services/editor/about.service.js";
import PersonalEvaluation from "./PersonalEvaluation.vue";
import RecordVideo from "./components/RecordVideo.vue";
import VxTooltip from "../../layouts/components/vx-tooltip/VxTooltip.vue";
import { successNotifyService, failureNotifyService } from "@/services/errors.service.js";
import UserProfilePicture from "../components/UserProfilePicture.vue";
import axios from "axios";

export default {
  components: {
    FeatherIcon,
    Options,
    PersonalEvaluation,
    RecordVideo,
    VuePerfectScrollbar,
    VxTooltip,
    UserProfilePicture,
  },
  name: "EditorAbout",
  data() {
    return {
      localData: {},
      organizationIds: "",
      constants,
      recordVideoPitch: false,
      settings: {
        suppressScrollX: true,
        maxScrollbarLength: 100,
      },
      editProfilePicturePrompt: false,
      tempProfilePicture: null,
      upload_success: false,
      fData: new FormData(),
      fPersonalityData: new FormData(),
      maxDate: "",
    };
  },
  computed: {
    otpSent() { return this.$store.state.auth.otpSent; },
    saveInProgress() { return this.$store.state.editor.saveInProgress; },
    isUserLoggedIn() { return this.$store.state.auth.isUserLoggedIn; },
    usersData() { return this.$store.state.users.data; },
    userId() { return this.$store.state.auth.user._id; },
    internetConnectionStatus() { return this.$store.state.users.internetConnectionStatus; },
    disableInputField() { return !this.isUserLoggedIn || !this.internetConnectionStatus; },
    organizationId() { return this.$store.state.organization.orgData._id; },
    organizationSlug() { return this.$store.state.organization.organizationSlug; },
  },
  watch: {
    usersData(newVal) {
      if (newVal && newVal._id && newVal.email) {
        this.localData = JSON.parse(JSON.stringify(newVal));
        if (this.localData.dob) {
          this.localData.dob = this.localData.dob.split("T")[0];
        }
      } else {
        console.warn("Ignored corrupted usersData update from store:", newVal);
      }
    },
    isUserLoggedIn(newVal) {
      if (newVal) {
        this.$store.commit("auth/SET_OTP_STATUS", true);
      }
    },
  },
  methods: {
    handlePictureUpdate(newImageUrl) {
      this.$set(this.localData, 'profilePictureUrl', newImageUrl);
      this.$vs.notify({
        title: 'Success',
        text: 'Profile picture updated successfully',
        color: 'success',
        iconPack: 'feather',
        icon: 'icon-check-circle'
      });
    },
    toggleVideoPitch() {
      if (this.recordVideoPitch) this.recordVideoPitch = false;
      else this.recordVideoPitch = true;
    },
    sendMeOTP() {
      const payload = { email: this.localData.email, password: this.localData.email, currentThis: this, userSource: "editor" };
      sendMeOTPService(payload);
    },
    verifyOTP() {
      const payload = { email: this.localData.email, otp: this.localData.otp, currentThis: this, userSource: "editorAbout" };
      verifyOTPService(payload);
    },
    closeEditProfilePicturePrompt() { this.$forceUpdate(); },
    openEditProfilePicturePrompt(input) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.tempProfilePicture = e.target.result;
        this.editProfilePicturePrompt = true;
      };
      reader.readAsDataURL(input.target.files[0]);
    },
    cropAndUploadProfilePicture() {},
    // Left original method here as it was in production, though unused by template
    async updateProfilePicture(input) {
      const payload = { imgInput: input, _id: this.userId, maxSize: 1 };
      this.$vs.loading();
      try {
        const response = await updateImageService(payload);
        if (response && response.data) {
          this.$store.dispatch("users/setUsersData", response.data);
          successNotifyService({ title: "Success", text: "Profile picture updated successfully!", currentThis: this });
        } else {
          throw new Error("Invalid response from the server after image upload.");
        }
      } catch (error) {
        console.error("Error updating profile picture:", error);
        failureNotifyService({ title: "Upload Failed", text: "Could not update your profile picture. Please try again.", currentThis: this });
      } finally {
        this.$vs.loading.close();
      }
    },
    async saveField(action, fieldName = null) {
      let fieldIsValid = false;
      if (fieldName) {
        fieldIsValid = await this.$validator.validate(fieldName);
        if (!fieldIsValid) return;
      } else {
        fieldIsValid = true;
      }
      if (fieldName && !fieldIsValid) return;
      
      if (!this.organizationId) {
        if (!(this.localData.personality[0].personalityYoulabId && this.localData.personality[0].personalityYoulabToken)) {
          if (this.localData.firstName && this.localData.lastName) {
            let youlabTokenApiData = {
              email: this.localData.email,
              first_name: this.localData.firstName,
              last_name: this.localData.lastName,
              _id: this.localData.personality[0]._id,
              flag: "youlabTokenGenerate",
            };
            this.$store.dispatch("editor/updatePersonality", youlabTokenApiData).then((res) => {
              this.localData.personality[0] = res.data;
            }).catch((err) => { throw err; });
          }
        }
      }
      const payload = { isUserLoggedIn: this.isUserLoggedIn, action, localData: this.localData, userId: this.userId, currentThis: this };
      saveInputFieldService(payload);
    },
    selectFieldPopup() {
      let file = this.$refs.file.files[0];
      this.$emit("parserpopup", { popup: true, file: file });
    },
    getTodayDate() {
      var d = new Date(), month = "" + (d.getMonth() + 1), day = "" + d.getDate(), year = d.getFullYear();
      if (month.length < 2) month = "0" + month;
      if (day.length < 2) day = "0" + day;
      return (this.maxDate = [year, month, day].join("-"));
    },
  },
  mounted() {
    this.getTodayDate();
  },
  created() {
    this.$store.dispatch("editor/toggleRightBar", true);
    this.$store.dispatch("editor/togglePreviewMode", false);

    if (this.isUserLoggedIn) {
      if (Object.keys(this.usersData).length) {
        this.localData = JSON.parse(JSON.stringify(this.usersData));
        if (this.localData.dob) {
          this.localData.dob = this.localData.dob.split("T")[0];
        }
      }
      this.$store.commit("auth/SET_OTP_STATUS", true);
    }
  },
  beforeDestroy() {
    this.localData = null;
    this.recordVideoPitch = false;
    this.constants = null;
    this.settings = null;
  },
};
</script>

<style scoped>
.scroll-area { position: relative; margin: auto; width: 100%; height: 100vh; }
.video-pitch { background: #ffffff; border: 1px dashed #f29200; box-sizing: border-box; border-radius: 12px; padding: 5px; width: 100%; height: 100%; cursor: pointer; }
.video-pitch--pitch { background: rgba(242, 147, 0, 0.1); border-radius: 12px; width: 100%; height: 100%; padding: 3rem; }
.video-pitch--pitch p:nth-child(2) { font-style: normal; font-weight: 600; font-size: 16px; line-height: 19px; text-transform: capitalize; color: #f29200; flex: none; order: 0; flex-grow: 0; margin: 2px 4px; }
.video-pitch--pitch p:nth-child(3) { font-family: Inter; font-style: normal; font-weight: normal; font-size: 14px; line-height: 17px; color: rgba(242, 146, 0, 0.6); flex: none; order: 1; flex-grow: 0; margin: 0px 4px; }
.profile-picture { position: relative; width: 8rem; height: 8rem; }
.upload-btn { position: absolute; margin: 2.65rem; background: rgba(255, 255, 255, 0.35) !important; }
.upload-btn-wrapper { position: relative; overflow: hidden; display: inline-block; cursor: pointer; }
.btn { color: gray; padding: 6em 3em; cursor: pointer; background: rgba(242, 147, 0, 0.06); border: 1px dashed #f29300; box-sizing: border-box; border-radius: 12px; font-style: normal; font-weight: 500; font-size: 16px; line-height: 19px; color: #848199; }
.upload-success { background-color: lightgreen !important; }
.upload-btn-wrapper input[type="file"] { font-size: 100px; position: absolute; left: 0; top: 0; opacity: 0; cursor: pointer; }
input { cursor: pointer !important; }
.dark-input-field >>> .vs-con-input .vs-input--input { background: #edededab !important; }
.vs-con-textarea >>> .vs-textarea { background: #edededab !important; }
.upload-resume-icon { position: absolute; margin-left: auto; margin-right: auto; left: 0; right: 0; text-align: center; margin-top: 40px; }
.resume-parser-text { top: 7em; }
.resume-parser-text p:nth-child(1) { font-style: normal; font-weight: 600; font-size: 16px; line-height: 19px; text-transform: capitalize; color: #f29200; flex: none; order: 0; flex-grow: 0; margin: 2px 4px; top: 2.5em; }
.resume-parser-text p:nth-child(2) { font-family: Inter; font-style: normal; font-weight: normal; font-size: 14px; line-height: 17px; color: rgba(242, 146, 0, 0.6); flex: none; order: 1; flex-grow: 0; margin: 0px 4px; top: 3.5em; }
</style>